import type { ComponentProps } from 'react';

import { cn } from '@repo/lib/cn';
import { Drawer as DrawerPrimitive } from 'vaul';

export function Drawer(drawerProps: ComponentProps<typeof DrawerPrimitive.Root>) {
  const { ...props } = drawerProps;
  return <DrawerPrimitive.Root data-slot="drawer" {...props} />;
}

export function DrawerTrigger(drawerTriggerProps: ComponentProps<typeof DrawerPrimitive.Trigger>) {
  const { ...props } = drawerTriggerProps;
  return <DrawerPrimitive.Trigger data-slot="drawer-trigger" {...props} />;
}

export function DrawerPortal(drawerPortalProps: ComponentProps<typeof DrawerPrimitive.Portal>) {
  const { ...props } = drawerPortalProps;
  return <DrawerPrimitive.Portal data-slot="drawer-portal" {...props} />;
}

export function DrawerClose(drawerCloseProps: ComponentProps<typeof DrawerPrimitive.Close>) {
  const { ...props } = drawerCloseProps;

  return <DrawerPrimitive.Close data-slot="drawer-close" {...props} />;
}

export function DrawerOverlay(drawerOverlayProps: ComponentProps<typeof DrawerPrimitive.Overlay>) {
  const { className, ...props } = drawerOverlayProps;

  return (
    <DrawerPrimitive.Overlay
      data-slot="drawer-overlay"
      className={cn('data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50', className)}
      {...props}
    />
  );
}

export function DrawerContent(drawerContentProps: ComponentProps<typeof DrawerPrimitive.Content>) {
  const { className, children, ...props } = drawerContentProps;
  return (
    <DrawerPortal data-slot="drawer-portal">
      <DrawerOverlay />
      <DrawerPrimitive.Content
        data-slot="drawer-content"
        className={cn(
          'group/drawer-content bg-background fixed z-50 mt-12 flex h-auto flex-col',
          'data-[vaul-drawer-direction=top]:inset-x-0 data-[vaul-drawer-direction=top]:top-0 data-[vaul-drawer-direction=top]:mb-24 data-[vaul-drawer-direction=top]:max-h-[calc(100dvh-var(--spacing)*4)] data-[vaul-drawer-direction=top]:rounded-b-lg data-[vaul-drawer-direction=top]:border-b',
          'data-[vaul-drawer-direction=bottom]:inset-x-0 data-[vaul-drawer-direction=bottom]:bottom-0 data-[vaul-drawer-direction=bottom]:mt-24 data-[vaul-drawer-direction=bottom]:max-h-[calc(100dvh-var(--spacing)*4)] data-[vaul-drawer-direction=bottom]:rounded-t-lg data-[vaul-drawer-direction=bottom]:border-t',
          'data-[vaul-drawer-direction=right]:inset-y-0 data-[vaul-drawer-direction=right]:right-0 data-[vaul-drawer-direction=right]:w-3/4 data-[vaul-drawer-direction=right]:border-l data-[vaul-drawer-direction=right]:sm:max-w-sm',
          'data-[vaul-drawer-direction=left]:inset-y-0 data-[vaul-drawer-direction=left]:left-0 data-[vaul-drawer-direction=left]:w-3/4 data-[vaul-drawer-direction=left]:border-r data-[vaul-drawer-direction=left]:sm:max-w-sm',
          className
        )}
        {...props}
      >
        <div className="bg-muted mx-auto mt-4 hidden h-2 w-[100px] shrink-0 rounded-full group-data-[vaul-drawer-direction=bottom]/drawer-content:block" />
        {children}
      </DrawerPrimitive.Content>
    </DrawerPortal>
  );
}

export function DrawerHeader(drawerHeaderProps: ComponentProps<'div'>) {
  const { className, ...props } = drawerHeaderProps;
  return <div data-slot="drawer-header" className={cn('flex flex-col gap-2 p-4', className)} {...props} />;
}

export function DrawerBody(drawerBodyProps: ComponentProps<'div'>) {
  const { className, ...props } = drawerBodyProps;
  return <div data-slot="drawer-body" className={cn('flex flex-col overflow-auto', className)} {...props} />;
}

export function DrawerFooter(drawerFooterProps: ComponentProps<'div'>) {
  const { className, ...props } = drawerFooterProps;

  return <div data-slot="drawer-footer" className={cn('mt-auto flex flex-row gap-2 p-4', className)} {...props} />;
}

export function DrawerTitle(drawerTitleProps: ComponentProps<typeof DrawerPrimitive.Title>) {
  const { className, ...props } = drawerTitleProps;
  return <DrawerPrimitive.Title data-slot="drawer-title" className={cn('text-text-primary text-lg leading-none font-semibold tracking-tight', className)} {...props} />;
}

export function DrawerDescription(drawerDescriptionProps: ComponentProps<typeof DrawerPrimitive.Description>) {
  const { className, ...props } = drawerDescriptionProps;
  return <DrawerPrimitive.Description data-slot="drawer-description" className={cn('text-accent-foreground text-sm', className)} {...props} />;
}
